/**
 * useShootOutputUploadCleanup — deletes the uploads of a line that is now made from the shoot (QA-653).
 *
 * A Photo staging or Slideshow line uploaded on its own keeps its files when the client then adds Photos. Placement
 * sends no `inputImages` for it, but the backend binds every session upload of the product to its assignment, so the
 * order would still stage the old uploads instead of the photos picked in the gallery. Deleting them through the same
 * route the drawer uses clears the session record, so what is placed is what the Summary shows.
 *
 * A file is deleted once its upload tile is in the scope: a resumed draft seeds the tile first (`useResumedUploads`),
 * and unclaiming before that would leave the session record behind. A delete that fails puts the tile back as an
 * upload; it is asked for once more, since a file left in the session would be staged instead of the shoot's photos.
 */

import React, { useEffect, useRef } from 'react'

import { FileAction } from 'components/common/FileAPI'
import { usePfCatalogue } from 'dataQueries/purchaseFlowRevamp.query'
import { logger } from 'utils/logging'
import { usePfStore } from '../store'
import { shootOutputLines } from '../store/shootOutput'
import { isOwnedByOtherLine, uploadedFilesForProductCode, usePfV2VisualUpload } from './kinds/stagingVisuals'

interface DeleteRequest {
  attempts: number
  /** The tile has shown the delete in progress. */
  deleting: boolean
  /** The tile came back as an upload after deleting: the delete failed. */
  failed: boolean
}

const MAX_DELETE_ATTEMPTS = 2

export function useShootOutputUploadCleanup(): void {
  const items = usePfStore((state) => state.items)
  const attachments = usePfStore((state) => state.attachments)
  const unclaimAttachment = usePfStore((state) => state.unclaimAttachment)
  const shells = usePfCatalogue().data?.shellProducts
  const { stagingEntries, deleteVisual } = usePfV2VisualUpload()
  // Deletes asked for, by filename: unclaiming re-runs the effect before the scope shows the delete, so a file is
  // asked for again only once its tile has been deleting and came back as an upload (a failed delete).
  const requested = useRef(new Map<string, DeleteRequest>())

  useEffect(() => {
    requested.current.forEach((request, filename) => {
      const entry = stagingEntries.find((candidate) => candidate.gcFilename === filename)
      if (entry?.action === FileAction.DELETE) request.deleting = true
      else if (entry && request.deleting) {
        request.deleting = false
        request.failed = true
        if (request.attempts >= MAX_DELETE_ATTEMPTS) {
          // Left in the session: the backend would stage this upload instead of the shoot's photos.
          logger.error('PF revamp: could not delete an upload of a line now made from the shoot', undefined, {
            context: { component: 'useShootOutputUploadCleanup', action: 'delete_failed', metadata: { filename } },
          })
        }
      }
    })
    const mayAsk = (filename: string) => {
      const request = requested.current.get(filename)
      return !request || (request.failed && request.attempts < MAX_DELETE_ATTEMPTS)
    }

    const fromShoot = shootOutputLines(items, shells)
    // Files another line still uses are not this line's to delete.
    const keptByOtherLines = new Set(
      Object.entries(attachments)
        .filter(([cartItemId]) => !fromShoot.has(cartItemId))
        .flatMap(([, files]) => files.map((file) => file.gcFilename))
    )
    fromShoot.forEach((use, cartItemId) => {
      const item = items.find((candidate) => candidate.id === cartItemId)
      if (!item) return
      // The line's claimed files, and any upload of its product it never claimed: the backend binds both.
      const owned = new Set([
        ...(attachments[cartItemId] ?? []).map((file) => file.gcFilename),
        ...uploadedFilesForProductCode(stagingEntries.filter((entry) => !isOwnedByOtherLine(entry, cartItemId)), item.shellProductId)
          .map((file) => file.gcFilename),
      ])
      const deletable = Array.from(owned)
        .filter((filename) => !keptByOtherLines.has(filename) && mayAsk(filename))
        .flatMap((filename) => {
          const entry = stagingEntries.find((candidate) => candidate.gcFilename === filename && candidate.action !== FileAction.DELETE)
          return entry ? [{ filename, entryId: entry.id }] : []
        })
      if (deletable.length === 0) return
      logger.info('PF revamp: deleting uploads of a line now made from the shoot', undefined, {
        context: { component: 'useShootOutputUploadCleanup', action: 'delete_uploads', metadata: { cartItemId, use, count: deletable.length } },
      })
      deletable.forEach(({ filename, entryId }) => {
        const attempts = (requested.current.get(filename)?.attempts ?? 0) + 1
        if (attempts === MAX_DELETE_ATTEMPTS) {
          logger.warn('PF revamp: retrying the delete of an upload of a line now made from the shoot', undefined, {
            context: { component: 'useShootOutputUploadCleanup', action: 'retry_delete', metadata: { cartItemId, filename } },
          })
        }
        requested.current.set(filename, { attempts, deleting: false, failed: false })
        unclaimAttachment(filename)
        deleteVisual(entryId)
      })
    })
  }, [items, shells, attachments, stagingEntries, unclaimAttachment, deleteVisual])
}

/** Mount-point for {@link useShootOutputUploadCleanup}; must sit inside `PfV2VisualUploadProvider`. */
export const ShootOutputUploadCleanup: React.FC = () => {
  useShootOutputUploadCleanup()
  return null
}
